/**
 * Realtime channels that open and close on their own (security item 9, 2026-10-19).
 *
 * Two traps in realtime-js 2.11.10, which every channel in this app goes through:
 *
 * 1. `supabase.removeChannel(channel)` (RealtimeClient.removeChannel) keeps the channels for which
 *    `c._joinRef !== channel._joinRef`. `_joinRef` is a prototype METHOD every channel shares, so
 *    the comparison is always false: the call empties the client's whole channel list and, finding
 *    it empty, disconnects the socket with its close handler switched off. Every other live channel
 *    is orphaned: no error, no reconnect, no rejoin. Leaving /licenses killed the notification
 *    bell's channel; leaving the projects list killed whatever had just subscribed.
 *    `leaveRealtimeChannel` leaves one channel (`channel.unsubscribe()`); the client drops it by
 *    TOPIC (`RealtimeClient._remove`) once the server confirms, or at once when the socket is down.
 *
 * 2. `supabase.channel(topic)` hands back the channel already registered under that topic, even one
 *    still leaving. A quick remount (StrictMode, back and forth between pages) got the closing
 *    channel, and its `.subscribe()` threw "tried to subscribe multiple times".
 *    `uniqueChannelTopic` gives every subscription a topic of its own, which also keeps (1) from
 *    dropping anyone else's channel.
 *
 * So: open with `supabase.channel(uniqueChannelTopic("…"))`, close with `leaveRealtimeChannel`, and
 * never call `supabase.removeChannel()` or `removeAllChannels()`. For `postgres_changes` the topic
 * is only the client's handle; what a channel receives is set by its bindings, not its name.
 */
import type { RealtimeChannel } from "@supabase/supabase-js";

let topicSeq = 0;

/** `base` with a suffix no other channel opened by this page has used. */
export function uniqueChannelTopic(base: string): string {
  topicSeq += 1;
  return `${base}-${topicSeq}`;
}

/** Leave this one channel; every other channel keeps running. */
export function leaveRealtimeChannel(channel: RealtimeChannel): void {
  void channel.unsubscribe();
}
